import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { Invitation } from "@event-planner/shared";
import {
  deriveInvitationEmailState,
  EMAIL_STATE_COLOR,
  EMAIL_STATE_LABEL,
} from "../utils/invitationEmailState";

interface InvitationEmailStatusChipProps {
  invitation: Invitation | null;
  /** Appended to the data-testid so rows stay individually addressable. */
  testIdSuffix: string;
}

/**
 * Read-only status chip for the roster's Email column. Actions (send,
 * preview, mark manually) live in the row menu, not on the chip.
 */
export function InvitationEmailStatusChip({
  invitation,
  testIdSuffix,
}: InvitationEmailStatusChipProps) {
  if (!invitation) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  const summary = deriveInvitationEmailState(invitation);
  const lines: string[] = [];
  if (summary.lastSentAt) {
    lines.push(
      `Last sent ${summary.lastSentAt.toLocaleString()} (${summary.sendCount} ${summary.sendCount === 1 ? "send" : "sends"})`,
    );
  }
  if (summary.markedManuallyAt) {
    lines.push(`Marked sent manually ${summary.markedManuallyAt.toLocaleString()}`);
  }
  if (summary.state === "failed" && summary.error) {
    lines.push(`Error: ${summary.error}`);
  }
  if (summary.state === "suppressed") {
    lines.push("Sending is switched off on the server (SUPPRESS_EMAILS). No email went out.");
  }
  if (summary.state === "sending") {
    lines.push("Handed to the mail service; waiting for confirmation.");
  }
  if (summary.state === "delivered") {
    lines.push("Accepted by the mail server. A bounce would show here as Failed within a few minutes.");
  }
  if (lines.length === 0) lines.push("No invitation email has been sent.");

  const testState =
    summary.state === "not_sent" || summary.state === "failed" || summary.state === "suppressed"
      ? "not-sent"
      : "sent";

  return (
    <Tooltip
      title={
        <span style={{ whiteSpace: "pre-line" }}>{lines.join("\n")}</span>
      }
      arrow
    >
      <Chip
        label={EMAIL_STATE_LABEL[summary.state]}
        size="small"
        color={EMAIL_STATE_COLOR[summary.state]}
        variant="outlined"
        sx={{ fontSize: "0.813rem" }}
        data-testid={`attendee-email-${testState}-${testIdSuffix}`}
        data-email-state={summary.state}
      />
    </Tooltip>
  );
}
